import React from "react";
import { ClipLoader } from "react-spinners";
import RecordNotFound from "./RecordNotFound";
import FetchingError from "./FetchingError";
import { formatYYYY_MM_DD } from "../../utils/dateFormating";

const Table = ({
  columns,
  data,
  card = true, // Default to true for current behavior
  actionButtons,
  className,
  minWidth = "min-w-[700px]",
  colomnClassName = "h-[56px] text-level-4",
  headerClassName = "text-level-3",
  headerCellClassName = "",
  bodyCellClassName = "",
  actionCellClassName = "",
  fetchingErrorClassName = "",
  fetchingErrorContainerClassName = "",
  loading,
  error,
  emptyMessage,
}) => {
  const statusColors = {
    "Order In Progress": "text-medium-green",
    Completed: "text-primary",
    Resolved: "text-primary",
    "Order Delivered": "text-primary",
    "Pending Payment": "text-light-red",
    "Pending Writer": "text-light-red",
    "In Revision": "text-light-red",
    "Complaint Raised": "text-light-red",
    Pending: "text-light-orange",
  };

  const shouldKeepColumnOnOneLine = (key = "") => {
    return ["amount", "price", "deadline", "date", "dueDate", "createdAt"].includes(key);
  };

  return (
    <div className="w-full">
      {/* For larger screens: Display as a table */}
      <div
        className={`overflow-auto scrollbar-thin max-h-[60.95vh] ${minWidth} rounded-lg border border-medium-gray ${className} ${
          card ? "hidden lg:block" : "block"
        }`}
      >
        <table className="table-auto w-full min-w-full border-collapse">
          {/* Table Header */}
          <thead
            className={` ${headerClassName} text-secondary font-inter font-semibold leading-5`}
          >
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  className={`text-left px-4 py-4 sticky top-0 bg-white border-b border-medium-gray ${
                    shouldKeepColumnOnOneLine(col.key) ? "whitespace-nowrap" : "whitespace-normal"
                  } ${headerCellClassName}`}
                >
                  {col.label}
                </th>
              ))}
              {/* Action Buttons Header */}
              {actionButtons && (
                <th className={`text-left pl-2 py-2 sticky top-0 bg-white border-b border-medium-gray ${headerCellClassName}`}>
                  Action Buttons
                </th>
              )}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-8">
                  <div className="flex justify-center">
                    <ClipLoader
                      color="#60a5fa"
                      loading={loading}
                      size={44}
                      speedMultiplier={0.5}
                    />
                  </div>
                </td>
              </tr>
            ) : error || data.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center">
                  <FetchingError
                    error={error}
                    isEmptyRecords={data.length === 0}
                    message={emptyMessage}
                    fetchingErrorClassName={fetchingErrorClassName}
                    fetchingErrorContainerClassName={fetchingErrorContainerClassName}
                  />
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`hover:bg-white ${
                    rowIndex % 2 === 0 ? "bg-white" : "bg-white"
                  }`}
                >
                  {columns.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-4 py-2 ${colomnClassName} text-gray font-inter leading-4 border-b border-medium-gray ${
                        shouldKeepColumnOnOneLine(col.key) ? "whitespace-nowrap" : "whitespace-normal break-words"
                      } ${
                        col.key === "status" ? statusColors[row[col.key]] : ""
                      } ${bodyCellClassName}`}
                    >
                      {col.render
                        ? col.render(row[col.key], row)
                        : row[col.key]}
                    </td>
                  ))}
                  {/* Action Buttons */}
                  {actionButtons && (
                    <td className={`px-4 py-2 border-b border-medium-gray whitespace-nowrap ${actionCellClassName || bodyCellClassName}`}>
                      {actionButtons(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* For smaller screens: Display as cards only if card=true */}
      {card && (
        <div className={`lg:hidden block space-y-4`}>
          {loading ? (
            <div className="flex justify-center items-center h-full w-full">
              <ClipLoader
                color="#60a5fa"
                loading={loading}
                size={44}
                speedMultiplier={0.5}
              />
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full w-full">
              <FetchingError error={error} message={emptyMessage} fetchingErrorClassName={fetchingErrorClassName} fetchingErrorContainerClassName={fetchingErrorContainerClassName}/>
            </div>
          ) : data.length === 0 ? (
            <div className="flex justify-center items-center h-full w-full">
              <FetchingError
                isEmptyRecords={true}
                message={emptyMessage}
                fetchingErrorClassName={fetchingErrorClassName}
                fetchingErrorContainerClassName={fetchingErrorContainerClassName}
              />
            </div>
          ) : (
            data.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="border border-medium-gray font-inter rounded-lg p-4 shadow-sm bg-white"
              >
                {/* Row 1: Payment Name and Amount */}
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-primary text-[15px] leading-[20px]">
                    {`Order - ${row?.order || "N/A"}`}
                  </span>
                  <span className="font-semibold text-secondary text-[15px] leading-[20px]">
                    {row.amount || "0.00"}
                  </span>
                </div>

                {/* Row 2: Payment Type */}
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-secondary text-[15px] leading-[20px]">
                    From{" "}
                    <span className="text-gray ">
                      {row?.paymentType || "N/A"}
                    </span>
                  </span>
                  <span className=" bg-light-blue px-2  rounded-[2px] text-primary">
                    {row.transactionType || "N/A"}
                  </span>
                </div>

                {/* Row 3: Payment ID */}
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-secondary text-[15px] leading-[20px]">
                    Pay ID{" "}
                    <span className="text-gray ">
                      {row?.paymentId || "N/A"}
                    </span>
                  </span>
                  <span className="font-semibold text-secondary text-[15px] leading-[20px]">
                    Paid at -{" "}
                    <span className="text-gray ">
                      {formatYYYY_MM_DD(row?.createdAt || "") || "N/A"}
                    </span>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Table;
